import type { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import { findTicket } from 'features/drilldown/panels/impl/niiLibraResultsTable/NiiLibraSharedTableColumns'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import {
  ConsequenceOfFailure,
  getCofModifier,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import {
  buildZoneOverviewRow,
  OverviewRow,
} from 'features/libra/stepComponents/zoneAnalysis/useComputedZoneData'
import {
  parseCofFromFieldValues,
  parseConfidenceFromFieldValues,
  parseZonesFromFieldValues,
  ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'

// Hardcoded because the drilldown doesn't have a ZoneAnalysisConfig the way
// Libra's `useConfidenceFromScreening` does; every NII ticket uses this field.
const NII_CONFIDENCE_FIELD_NAME =
  'confidenceInPredictingDegradationTypeAndLocation'

const ROW_ID_SEPARATOR = '::'
const NO_ZONE_ROW_SUFFIX = '__no_zone__'

export type LibraNiiZoneRowContext = ZoneParams

function getLibraTicketDataField<T>(
  data: unknown,
  field: string,
  fallback: T
): T {
  if (!data || typeof data !== 'object') return fallback
  const value = (data as Record<string, unknown>)[field]
  return typeof value === typeof fallback ? (value as T) : fallback
}

function getTicketVersion(data: unknown): number {
  const version = getLibraTicketDataField(data, 'version', 0)
  return Number.isFinite(version) ? version : 0
}

function getTicketAssessmentId(ticketId: string, data: unknown): string {
  return getLibraTicketDataField(data, 'assessmentId', ticketId)
}

function readTicketFieldValues(data: unknown): FieldValues | undefined {
  if (!data || typeof data !== 'object') return undefined
  const fieldValues = (data as { fieldValues?: unknown }).fieldValues
  if (!fieldValues || typeof fieldValues !== 'object') return undefined
  return fieldValues as FieldValues
}

type TicketLike = { id: string; data: unknown }

type ParsedTicket = {
  zones: ReadonlyArray<ZoneParams>
  cof: ConsequenceOfFailure
  confidence: 'low' | 'med' | 'high'
}

/**
 * One unified cache keyed by ticket id. The drilldown is read-only — no edits
 * land here — so a single id key is sufficient. `zoneParamsSchema` mints fresh
 * UUIDs for zones missing an `id`, so caching also keeps composite row ids
 * stable across the expander and the per-cell readers.
 */
const parsedTicketCache = new Map<string, ParsedTicket>()

function getParsedTicket(ticket: TicketLike): ParsedTicket {
  const cached = parsedTicketCache.get(ticket.id)
  if (cached) return cached
  const fieldValues = readTicketFieldValues(ticket.data) ?? {}
  const parsed: ParsedTicket = {
    zones: parseZonesFromFieldValues(fieldValues).zones,
    cof: parseCofFromFieldValues(fieldValues).cof,
    confidence: parseConfidenceFromFieldValues(
      fieldValues,
      NII_CONFIDENCE_FIELD_NAME
    ),
  }
  parsedTicketCache.set(ticket.id, parsed)
  return parsed
}

/**
 * Cached drilldown wrappers around Libra's pure `parseXFromFieldValues`
 * helpers — each one returns the same value the matching Libra hook
 * (`useZoneData` / `useCofData` / `useConfidenceFromScreening`) would.
 */
export function parseZonesFromTicket(
  ticket: TicketLike
): ReadonlyArray<ZoneParams> {
  return getParsedTicket(ticket).zones
}

export function parseCofFromTicket(ticket: TicketLike): ConsequenceOfFailure {
  return getParsedTicket(ticket).cof
}

export function parseConfidenceFromTicket(
  ticket: TicketLike
): 'low' | 'med' | 'high' {
  return getParsedTicket(ticket).confidence
}

/**
 * Builds the per-zone overview row for `rec` by delegating to Libra's pure
 * `buildZoneOverviewRow` — the same helper that backs `useComputedZoneData`
 * — so the drilldown table renders the exact same coverage % / area strings
 * Libra renders in-app. The stored `zone.coverage_calculation` is
 * intentionally bypassed because it can be stale relative to the current
 * cof / screening confidence.
 */
export function computeZoneOverviewRow(
  rec: PaginatedRolodexTableRecordType
): OverviewRow | undefined {
  const zone = readLibraNiiZoneFromRow(rec)
  if (!zone) return undefined
  const ticket = findTicket(rec.entries)
  if (!ticket) return undefined
  const zones = parseZonesFromTicket(ticket)
  const idx = zones.findIndex((z) => z.id === zone.id)
  const cof = parseCofFromTicket(ticket)
  const confidence = parseConfidenceFromTicket(ticket)
  return buildZoneOverviewRow(
    zone,
    idx >= 0 ? idx : 0,
    getCofModifier(cof.level),
    confidence
  )
}

function buildExpandedRowId(ticketId: string, zoneKey: string): string {
  return `${ticketId}${ROW_ID_SEPARATOR}${zoneKey}`
}

function parseZoneKeyFromRowId(rowId: string): string | null {
  const separatorIndex = rowId.lastIndexOf(ROW_ID_SEPARATOR)
  if (separatorIndex === -1) return null
  const suffix = rowId.slice(separatorIndex + ROW_ID_SEPARATOR.length)
  if (!suffix || suffix === NO_ZONE_ROW_SUFFIX) return null
  return suffix
}

export function readLibraNiiZoneFromRow(
  rec: PaginatedRolodexTableRecordType
): LibraNiiZoneRowContext | null {
  const zoneKey = parseZoneKeyFromRowId(rec.id)
  if (!zoneKey) return null
  const ticket = findTicket(rec.entries)
  if (!ticket) return null
  return parseZonesFromTicket(ticket).find((z) => z.id === zoneKey) ?? null
}

/** One table row per zone on each libra-ticket (pagination remains ticket-scoped). */
export function expandLibraTicketRecordsToZoneRows(
  rows: ReadonlyArray<PaginatedRolodexTableRecordType>
): ReadonlyArray<PaginatedRolodexTableRecordType> {
  const out: PaginatedRolodexTableRecordType[] = []
  for (const row of rows) {
    const ticket = findTicket(row.entries)
    if (!ticket) {
      out.push(row)
      continue
    }
    const zones = parseZonesFromTicket(ticket)
    if (zones.length === 0) {
      out.push({
        ...row,
        id: buildExpandedRowId(ticket.id, NO_ZONE_ROW_SUFFIX),
      })
      continue
    }
    for (const zone of zones) {
      out.push({
        ...row,
        id: buildExpandedRowId(ticket.id, zone.id),
      })
    }
  }
  return out
}

export function filterLatestCompleteLibraTicketRows(
  rows: ReadonlyArray<PaginatedRolodexTableRecordType>
): ReadonlyArray<PaginatedRolodexTableRecordType> {
  const latestCompleteByAssessmentId = new Map<
    string,
    { row: PaginatedRolodexTableRecordType; version: number }
  >()

  for (const row of rows) {
    const ticket = findTicket(row.entries)
    if (!ticket) {
      continue
    }
    const isComplete = getLibraTicketDataField(
      ticket.data,
      'workflowComplete',
      false
    )
    if (!isComplete) {
      continue
    }
    const assessmentId = getTicketAssessmentId(ticket.id, ticket.data)
    const version = getTicketVersion(ticket.data)
    const existing = latestCompleteByAssessmentId.get(assessmentId)
    if (!existing || version > existing.version) {
      latestCompleteByAssessmentId.set(assessmentId, { row, version })
    }
  }

  return rows.filter((row) => {
    const ticket = findTicket(row.entries)
    if (!ticket) {
      return true
    }
    const assessmentId = getTicketAssessmentId(ticket.id, ticket.data)
    return latestCompleteByAssessmentId.get(assessmentId)?.row === row
  })
}

export function expandLatestCompleteLibraTicketRecordsToZoneRows(
  rows: ReadonlyArray<PaginatedRolodexTableRecordType>
): ReadonlyArray<PaginatedRolodexTableRecordType> {
  return expandLibraTicketRecordsToZoneRows(
    filterLatestCompleteLibraTicketRows(rows)
  )
}
